import { useMutation } from '@tanstack/react-query';
import type { TrackEventInputUnion, TrackEventBasePayload, TrackingAppUnion } from 'app/data/types/tracking';
import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { TRACKING_APP } from 'app/shared/consts';
import { useUser } from 'app/shared/contexts/user';
import { useApi } from 'app/shared/contexts/api';

type TrackEventResponse = {
  message: string;
  statusCode: number;
};

type UseTrackEventMutationParams = {
  app?: TrackingAppUnion;
};

export const useTrackEventMutation = (params: UseTrackEventMutationParams = {}) => {
  const { app = TRACKING_APP } = params;
  const userDetails = useUser();
  const { apiUrl, getAuthHeaders } = useApi();

  const { mutate: trackEvent } = useMutation({
    mutationFn: async (input: TrackEventInputUnion) => {
      const eventPayload: TrackEventBasePayload = {
        app,
        timestamp: Date.now(),
        user_agent: navigator.userAgent,
        user_uuid: userDetails?.user?.uuid ?? '',
        ...input,
      };

      // Read the live token ref (like every sibling mutation) so a track POST fired in the
      // setAuthToken -> invalidateQueries refetch window carries the FRESH Bearer, not the stale state.
      const response = await apiRequest<TrackEventResponse, TrackEventBasePayload, undefined>({
        url: `${apiUrl}/tracking/track/v2`,
        method: HttpMethod.POST,
        headers: await getAuthHeaders(),
        body: eventPayload,
      });
      if (response.isEmpty()) return Promise.reject(new Error('Failed to track page impression'));
      return response.data.message;
    },
  });

  return { trackEvent };
};
